import {BASIS, MsgLabel, OSkinableInit} from "/@back@/commons/basis.js";
import {AppFrameDeskFeat, IApp, IAppCtx} from "/@back@/core/appFrame.js";
import {AppHeader} from "/@back@/core/widgets/appHeader.js";
import {DuplicateSrc, FocusItemOrSpace, ShortDescCopy} from "/@back@/wsp/actions/srcActions.js";
import {JWspAppDef, JWspDocAppDef} from "/@back@/wsp/plugins/wspsPlg.js";
import "/@back@/wsp/views/views_Perms.js";
import {Action, ActionMenu, IAccelKeyMgrPointer, IAction} from "/@lib@/commons/actions.js";
import {IInfo, IInfoConsumer, IInfoProducer, InfoBrokerBasic} from "/@lib@/commons/infos.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {IView, IViewApi, IViewsContainer, OViewVisitOptions, VIEWS, ViewsContainer} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IChainEnv} from "/@lib@/wsp/chain.js";
import {InfoCurrentItem, InfoFocusItem, InfoFocusNodeInItem, InfoSelectUris, IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {JSrcFields, JSrcFieldsTree, SRC, srcRef, srcUri} from "/@lib@/wsp/src.js";
import {IWspEnv, IWspUiEnv, JWspUriChangeMsg, WSP} from "/@lib@/wsp/wsp.js";
import {ILastDatasBuilder, JLastDatas, LASTDATAS} from "/@lib@/commons/lastDatas.js";
import {IItemUiEnv, IItemViewer} from "/@back@/wsp/views/itemMain.js";
import {FastFindItem, OFastFindItemsInit, ReqFastFind} from "/@back@/wsp/dialogs/fastFindItem.js";
import {Toc} from "/@back@/wsp/views/toc.js";
import {Generators} from "/@back@/wsp/views/generators.js";
import {SRCDRAWER, SrcDrawer, SrcDrawerInline} from "/@back@/wsp/widgets/srcDrawer.js";
import {ItemViewerSingle} from "/@back@/wsp/views/itemViewerSingle.js";
import {SearchAnd, SearchItemModel, SearchLinkChildren, SearchLinkParents, SearchOccurencesSubExp, SearchPath, SearchRequest} from "/@lib@/wsp/search.js";
import {SpaceTreeDataSearch} from "/@back@/wsp/views/spaceTree.js";
import {GridColTreeDef} from "/@back@/commons/widgets/tree.js";
import {CellBuilderSrcIconCodeTitle} from "/@back@/wsp/widgets/srcGridColumns.js";
import {Grid} from "/@back@/commons/widgets/grid-tags.js";
import {GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {ActionBtn, Button} from "/@back@/commons/widgets/buttons.js";
import {ItemCreator} from "/@back@/wsp/dialogs/itemCreator.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {ItemType} from "/@lib@/wsp/wspMetaUi.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {ItemEditInDialogInfoBroker, ItemXmlEd} from "/@back@/wsp/views/item/itemXmlEd.js";
import {LANG} from "/@lib@/commons/lang.js";
import {IWedEditor} from "/@back@/edit/wed/wedEditor.js";
import {Resizer} from "/@back@/commons/widgets/resizer.js";
import {registerWspProtocolsActions} from "/@back@/wsp/actions/scProtocolActions.js";
import "/@back@/wsp/apps/apps_Perms.js";
import {WspBaseApp} from "/@back@/wsp/apps/wspBaseApp.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {IPopupable} from "/@back@/commons/widgets/popupable.js";

export interface OWspDocAppInit extends OSkinableInit, IAppCtx<IChainEnv> {
	reg?: IReg<IChainEnv>
	itemViewer?: IItemViewer
	appDef: JWspDocAppDef
	lastDatas?: JLDWspDoc
}

/** App d'accès complet à un atelier. */
export interface WspDocApp extends WspBaseApp {
	initialize(init: OWspDocAppInit): this;
}

export class WspDocApp extends WspBaseApp implements IApp<IWspEnv>, IViewsContainer, IInfoConsumer, IAccelKeyMgrPointer<IRegPointer<IWspUiEnv>>, ILastDatasBuilder {

	appDef: JWspDocAppDef;

	appHeader: AppHeader<WspDocApp>;

	docViewer: IItemViewer;

	protected _config: OWspDocAppInit;

	protected _docFinder: FindDocItem;

	protected _docHeader: HTMLElement;
	protected _docToggle: HTMLElement;
	protected _docRef: SrcDrawer;
	protected _docRoot: HTMLElement;
	protected _docMain: HTMLElement;
	_toc: Toc;

	//protected _supResizer: HTMLElement;
	protected _supHeader: HTMLElement;
	protected _supToggle: HTMLElement;
	protected _supRef: SrcDrawer;
	_supMain: HTMLElement;
	protected _supEd: ItemXmlEd;


	protected _genHeader: HTMLElement;
	protected _genToggle: HTMLElement;
	protected _gen: Generators;

	/** Api IShortDesc */
	shortDescs: JSrcFields[] = [];

	/** Api IShortDesc */
	get emitter() {return this}

	get isDocFinderShown(): boolean {return !this._docFinder.hidden}

	protected async _initialize(init: OWspDocAppInit): Promise<void> {
		this._config = init;
		this.appDef = init.appDef;
		this.universe = init.reg.env.universe;

		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.wsp = this.universe.wspServer.wspsLive.findWsp(this.appDef.wspDoc, true);
		return this._initApp(init);
	}

	/** Init ou RE-init de l'app. */
	protected async _initApp(init: OWspDocAppInit) {
		this.infoBroker = new WspDocAppInfoBroker(this);
		this.infoBroker.addConsumer(this);
		this.docViewer = null;
		const sr = this.shadowRoot;
		const pendingUi = new MsgLabel();

		if (!await this._initWsp(init, pendingUi)) return;

		if (!this.wsp.wspMetaUi.getDocModels()) {
			//Pas de vue de l'atelier par document disponible, on bascule sur la vue de l'atelier orienté "explorateur".
			Promise.resolve().then(() => new OpenWspApp().execute(this)); //en async pour finir l'init de cette App qui va ensuite être éliminée.
			return;
		}

		if (!await this._initReg(init, "wspDocApp", "ui.wspDocApp", pendingUi)) return;
		const reg = this.reg;

		BASIS.clearContent(sr);

		this._docFinder = sr.appendChild(new FindDocItem().initialize({
			reg,
			restrictions: new SearchItemModel(this.wsp.wspMetaUi.getDocModels())
		} as OFastFindItemsInit));
		this._docFinder.setAttribute("hidden", "");

		this._docToggle = <button class="toggle" onclick={this.onTogglePanel}><span class="headTi">Contenu</span></button> as HTMLButtonElement;
		this._docHeader = sr.appendChild(<div id="docHeader" class="headPanel" ondblclick={this.onTogglePanel}>
			<span class="countPanel">1</span>{this._docToggle}
		</div>);
		this._docRef = this._docHeader.appendChild(new SrcDrawerInline().initialize({
			reg,
			skin: 'wsp-doc-app/srcptrInHead',
			tpl: SRCDRAWER.itemCodeTpl
		}));
		this._docRef.onSrcRefChange.add(this.onDocRefChanged.bind(this));

		this._docHeader.appendChild(ActionBtn.buildButton(new Action<IRegPointer<IWspUiEnv>>("docChange")
			.setLabel("Changer de contenu...")
			.requireVisiblePerm("ui.app.wspDoc.change.doc")
			.setExecute((ctx) => {
				this.setDoc(null);
			}), this, 'dialog'));

		this._docHeader.appendChild(<span style="flex:1"/>);
		this._docHeader.appendChild(<BarActions id="docHeaderActions" î={{
			reg,
			actions: reg.getList("actions:wspDocApp:docHeader"),
			actionContext: this
		} as OBarActionsInit<WspDocApp>}/>);

		this._docRoot = sr.appendChild(<div id="docRoot" c-resizable=""/>);
		this._toc = this._docRoot.appendChild(<Toc c-resizable="" î={{
			reg,
			infoBroker: this.infoBroker,
			hideHeader: true
		}}/> as Toc);
		this._docRoot.appendChild(<c-resizer c-orient="row"/>);
		this._docMain = this._docRoot.appendChild(<div id="docMain" c-resizable=""/>);

		//this._supResizer = sr.appendChild(<c-resizer c-orient="column"/>);
		this._supToggle = <button class="toggle closed" onclick={this.onTogglePanel}><span class="headTi">Support</span></button> as HTMLButtonElement;
		this._supHeader = sr.appendChild(<div id="supHeader" class="headPanel" hidden ondblclick={this.onTogglePanel}>
			<span class="countPanel">2</span>{this._supToggle}
		</div>);

		this._supRef = this._supHeader.appendChild(<SrcDrawerInline id="supRef" î={{
			reg,
			skin: 'wsp-doc-app/srcptrInHead',
			tpl: SRCDRAWER.itemCodeTpl
		}}/>) as SrcDrawerInline;
		this._supRef.onSrcRefChange.add(this.onSupRefChanged.bind(this));

		this._supHeader.appendChild(<c-button id="selectSup" ui-context="dialog" role="menu" label="Sélectionner" onclick={this.selectSupport}/>);
		this._supHeader.appendChild(<c-button id="createSup" ui-context="dialog" label="Créer un support..." onclick={this.createSupport}/>);

		this._supMain = sr.appendChild(<div id="supMain" c-resizable="" hidden=""/>);

		sr.appendChild(<c-resizer c-orient="column"/>);
		this._genToggle = <button class="toggle closed" onclick={this.onTogglePanel}><span class="headTi">Publications</span></button> as HTMLButtonElement;
		this._genHeader = sr.appendChild(<div id="genHeader" class="headPanel" ondblclick={this.onTogglePanel}>
			<span class="countPanel">3</span>{this._genToggle}
		</div>);
		this._gen = sr.appendChild(<Generators c-resizable="" hidden="" î={{reg}}/> as Generators);

		//AppHeader (après les autres éléments pour déclarer les focusListening)
		await this._initAppHeader(init, [this._docRef, this._docFinder, this._toc, this._docMain, this._supRef, this._supMain, this._gen], "actions:wspDocApp:main", "actions:wspDocApp:more", "actions:wspDocApp:header:main", "actions:wspDocApp:header:more", "areas:wspDocApp:header");
		sr.insertBefore(this.appHeader, this._docFinder);

		//
		this._refreshTitle(null);
		this._refreshMigration();

		//Construction de la mainView
		this.docViewer = init.itemViewer || new ItemViewerSingle();

		//Chargement...
		let ld = init.lastDatas;
		let docRef = this.appDef.doc;
		if (docRef) {
			if (ld && ld.docRef !== docRef) ld = null;
		} else if (ld) {
			docRef = ld.docRef;
		}
		let srcRef = this.appDef.src;
		if (srcRef) {
			if (ld && ld.srcRef && ld.srcRef !== srcRef) ld = null;
		} else if (ld) {
			srcRef = ld.srcRef;
		}
		let supRef = this.appDef.sup;
		if (supRef) {
			if (ld && ld.supRef !== supRef) {
				ld.supEd = null;
				ld.genCd = null;
			}
		} else if (ld) {
			supRef = ld.supRef;
		}
		await this.initDocAndSup(docRef, supRef, srcRef, ld);
		if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
		if (ld && ld.closed) {
			this.setDocPanelState(ld.closed.indexOf('d') < 0);
			this.setGenPanelState(ld.closed.indexOf('g') < 0);
		}
		init.lastDatas = undefined;

		const injectLoads = this.reg.getList<(app: WspDocApp) => void | Promise<void>>("wspDocApp:load");
		if (injectLoads) for (const lstn of injectLoads) {
			const result = lstn(this);
			if (result instanceof Promise) await result;
		}
	}

	reloadApp() {
		this.onViewHidden(true);
		this.reinitializeStarted(this._initApp(this._config));
		VIEWS.onViewShown(this);
	}

	/** Affecte un nouveau doc, reset le sup en conséquence. */
	setDoc(srcRef: srcRef, src?: JSrcFields) {
		this._docRef.setSrcRef(srcRef, src);
		this._supRef.setSrcRef(null);
	}

	/** Affecte un nouveau support. ATTENTION : la compat avec le doc courant a dû être validé en amont. */
	setSup(srcRef: srcRef, src?: JSrcFields) {
		this._supRef.setSrcRef(srcRef, src);
	}

	/** (Re)Init simultannément le doc et son support avec contrôle de cohérence (et injection lastDatas). */
	async initDocAndSup(docRef: srcRef, supRef: srcRef, srcRef: srcRef, lastDatas?: JLDWspDoc) {
		this._toc.setRootSrcRef(docRef);
		if (!this.docViewer.reg) {
			//1er init
			await this.docViewer.initViewer(this.reg, this._docMain, lastDatas && lastDatas.docView, srcRef || docRef);
		} else {
			//Déjà init
			await this.docViewer.openSrcRef(srcRef || docRef);
		}
		if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
		this._docRef.setSrcRef(docRef);
		if (this._docRef.fetchPending) await this._docRef.fetchPending;
		if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
		if (supRef) {
			//on fetch le shortdesc de supRef en s'assurant qu'il est bien un support du doc.
			const req = new SearchRequest(this.wsp.getShortDescDef()).setWhere(
				new SearchAnd(
					new SearchPath(supRef),
					new SearchOccurencesSubExp('gt', 0, new SearchAnd(
						new SearchLinkChildren('.', 'support'),
						new SearchPath(docRef))
					)
				)
			);
			const supSds = await WSP.fetchSearchJson<JSrcFields>(this.wsp, this, req.toXml());
			if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
			if (supSds && supSds.length > 0) {
				const supSd = supSds[0];
				this._clearSupEditor();
				this._buildSupEditor(supSd.itModel, this._docRef.shortDesc.itModel, SRC.srcRef(supSd), lastDatas && lastDatas.supEd);
				this._supRef.setSrcRef(SRC.srcRef(supSd), supSd);
				this._gen.setSrc(supSd, lastDatas && lastDatas.genCd);
			} else {
				this._supRef.setSrcRef(null);
			}
		} else {
			this._supRef.setSrcRef(null);
		}
	}

	onViewHidden(closed?: boolean): void {
		if (this.initialized) {
			if (closed) this._closeApp("wspDocApp");
			VIEWS.onContainerHidden(this, closed);
			this._lastMigrPopup?.close();
			if (closed) this.reg?.close();
		} else {
			this.initializedAsync.then(() => this.onViewHidden(closed));
		}
	}

	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any {
		const onlyVisible = options && options.visible;
		if (this._docFinder && (!onlyVisible || !this._docFinder.hidden)) visitor(this._docFinder);
		visitor(this.docViewer);
		if (this._supEd && (!onlyVisible || !this._supEd.hidden)) visitor(this._supEd);
		if (this._toc && (!onlyVisible || !this._toc.hidden)) visitor(this._toc);
		if (this._gen && (!onlyVisible || !this._gen.hidden)) visitor(this._gen);
	}

	async visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		const onlyVisible = options && options.visible;
		if (this._docFinder && (!onlyVisible || !this._docFinder.hidden)) await visitor(this._docFinder);
		await visitor(this.docViewer);
		if (this._supEd && (!onlyVisible || !this._supEd.hidden)) await visitor(this._supEd);
		if (this._toc && (!onlyVisible || !this._toc.hidden)) await visitor(this._toc);
		if (this._gen && (!onlyVisible || !this._gen.hidden)) await visitor(this._gen);
	}

	updateAppDef(def: JWspDocAppDef): boolean {
		if (this.appDef === def) return true; //chgt issu d'ici
		//Si changement d'atelier on remplace totalement cette app.
		if (!def.wspDoc || def.wspDoc !== this.appDef.wspDoc || (def.u && def.u !== this.reg.env.universe.getId())) return false;
		this.appDef = def;
		if (this.initialized) {
			if (this.wsp && this.wsp.isAvailable) {
				this.onAppDefChange();
			} else {
				this.reloadApp();
			}
		} else {
			this.initializedAsync.then(() => this.onAppDefChange());
		}
		return true;
	}

	protected async onAppDefChange() {
		await this.initDocAndSup(this.appDef.doc, this.appDef.sup, this.appDef.src);
		this.refresh();
	}

	onInfo(info: IInfo): void {
		if (this.initialized) {
			if (info instanceof InfoCurrentItem) {
				//this.setDocPanelState(true); pb à l'init contrecarre les lastDatas.
				//Changement de doc => dispatch c-appdef-change
				const shortDesc = info.shortDesc;
				const srcRef = shortDesc ? SRC.srcRef(shortDesc) : null;
				if (this.appDef.src ? srcRef !== this.appDef.src : srcRef !== this.appDef.doc) {
					this.appDef.src = srcRef === this.appDef.doc ? undefined : srcRef;
					this.dispatchEvent(new CustomEvent('c-appdef-change', {bubbles: true}));
				}
			} else if (info instanceof InfoSelectUris) {
				if (this.isDocFinderShown) {
					this._docFinder.refetch();
				} // else... todo quand doc affiché ?
			}
		} else {
			this.initializedAsync.then(() => this.onInfo(info));
		}
	}

	buildLastDatas(parentLastDatas: JLDWspDoc): void {
		if (!this._docRef) return;
		parentLastDatas.docRef = this._docRef.srcRef;
		if (parentLastDatas.docRef) {
			parentLastDatas.docView = {};
			LASTDATAS.buildLastDatas(parentLastDatas.docView, this._docRoot);
		}
		parentLastDatas.supRef = this._supRef.srcRef;
		if (this._supEd) {
			parentLastDatas.supEd = {};
			LASTDATAS.buildLastDatas(parentLastDatas.supEd, this._supEd);
		}
		const genCd = this._gen.codePub;
		if (genCd) parentLastDatas.genCd = genCd;
		const closed = LANG.pushIfDefined<string>([], this._docToggle.classList.contains('closed') ? 'd' : undefined, this._supToggle.classList.contains('closed') ? 's' : undefined, this._genToggle.classList.contains('closed') ? 'g' : undefined)
		if (closed.length > 0) parentLastDatas.closed = closed.join('');
	}

	protected _refresh() {
		if (!this._docRef) return; //init interrompu.
		if (!this._docRef.srcRef) {
			// Affichage de la recherche du doc
			DOM.setHidden(this._docHeader, true);
			DOM.setHidden(this._docRoot, true);
			//DOM.setHidden(this._supResizer, true);
			DOM.setHidden(this._supHeader, true);
			DOM.setHidden(this._supMain, true);
			DOM.setHidden(this._genHeader, true);
			hideView(this._gen, true);
			hideView(this._docFinder, false);
			return;
		}
		//On a un doc
		hideView(this._docFinder, true);
		DOM.setHidden(this._docHeader, false);
		DOM.setHidden(this._docRoot, this._docToggle.classList.contains("closed"));

		const docSd = this._docRef.shortDesc;
		const docModel = this.wsp.wspMetaUi.getItemType(docSd.itModel);
		if (docModel.getSupportModels()) {
			//DOM.setHidden(this._supResizer, false);
			DOM.setHidden(this._supHeader, false);
			DOM.setHidden(this._supMain, !this._supRef.shortDesc || this._supToggle.classList.contains("closed"));
		} else {
			this._supRef.setSrcRef(null);
			//DOM.setHidden(this._supResizer, true);
			DOM.setHidden(this._supHeader, true);
			DOM.setHidden(this._supMain, true);
		}
		// Zone "Publications"
		const supSd = this._supRef.shortDesc;
		if (supSd) {
			// - Gens associés au support, si défini
			//if (supSd && this.wsp.wspMetaUi.getItemType(supSd.itModel).hasGenerators(this.reg.env.securityCtx)) { //On a des gen associés au support
			DOM.setTextContent(DOM.findFirstChild(this._genHeader, (n: Node): n is HTMLElement => (n as HTMLElement).className === "countPanel"), "3");
			DOM.setHidden(this._genHeader, false);
			hideView(this._gen, this._genToggle.classList.contains("closed"));
			if (this._gen.shortDesc !== supSd) this._gen.setSrc(supSd);
			// } else {
			// 	DOM.setHidden(this._genHeader, true);
			// 	DOM.setHidden(this._gen, true);
			// }
		} else if (docSd && docModel.hasGenerators(this.reg.env.securityCtx)) {
			// - Gens associés au docModel
			DOM.setTextContent(DOM.findFirstChild(this._genHeader, (n: Node): n is HTMLElement => (n as HTMLElement).className === "countPanel"), "2");
			DOM.setHidden(this._genHeader, false);
			hideView(this._gen, this._genToggle.classList.contains("closed"));
			if (this._gen.shortDesc !== docSd) this._gen.setSrc(docSd);
		} else {
			// - Pas de gens
			DOM.setHidden(this._genHeader, true);
			hideView(this._gen, true);
		}
	}

	protected onTogglePanel(this: HTMLElement, ev?: MouseEvent) {
		const me = DOMSH.findHost<WspDocApp>(this);
		const btn = this instanceof HTMLButtonElement ? this : DOM.findFirstChild(this, (n: Node): n is HTMLElement => (n as HTMLElement).classList.contains('toggle'));
		const st = btn.classList.toggle("closed");

		// Doc et Support en opposition.
		const header = btn.parentElement.id;
		if (header === "docHeader") {
			if (st) {
				me._supMain.style.flex = me._docRoot.style.flex;
			} else {
				me._docRoot.style.flex = me._supMain.style.flex;
			}
			me._supToggle.classList.toggle("closed", !st);
		} else if (header === "supHeader") {
			if (st) {
				me._docRoot.style.flex = me._supMain.style.flex;
			} else {
				me._supMain.style.flex = me._docRoot.style.flex;
			}
			me._docToggle.classList.toggle("closed", !st);
		}

		if (ev && !me._supHeader.hidden && !me._supRef.shortDesc && (!me._supToggle.classList.contains('closed') || !me._genToggle.classList.contains('closed'))) {
			//On a provoqué l'ouverture du panel support ou gen, et on a des supports, mais aucun support sel,
			// => on affiche la sel d'un support.
			me.selectSupport.call(me.shadowRoot.getElementById('selectSup'), ev);
		}

		me.refresh();
	}

	protected setDocPanelState(open: boolean) {
		if (this._docToggle.classList.contains("closed") === open) this.onTogglePanel.call(this._docToggle);
	}

	protected setSupPanelState(open: boolean) {
		if (this._supToggle.classList.contains("closed") === open) this.onTogglePanel.call(this._supToggle);
	}

	protected setGenPanelState(open: boolean) {
		if (this._genToggle.classList.contains("closed") === open) this.onTogglePanel.call(this._genToggle);
	}

	protected onDocRefChanged() {
		const docRef = this._docRef.srcRef;
		if (docRef && this._docRef.shortDesc.srcSt < 0) {
			//détection de la suppr de l'item.
			this.setDoc(null);
			return;
		}
		if (docRef && this.docViewer && this._toc.getRootSrcRef() === docRef) return;
		this._toc.setRootSrcRef(docRef);
		this.docViewer.openSrcRef(docRef);
		const sd = this._docRef.shortDesc;
		this.setDocPanelState(true);
		this.refresh();
		this._refreshTitle(sd ? ITEM.getSrcMainName(this.reg, sd, this.wsp.wspMetaUi) : null);
		//Changement de doc => dispatch c-appdef-change
		if (!docRef) {
			this.appDef.doc = undefined;
			this.appDef.src = undefined;
			this.appDef.sup = undefined;
		} else {
			this.appDef.doc = docRef;
			if (this.appDef.src === docRef) this.appDef.src = undefined;
		}
		this.dispatchEvent(new CustomEvent('c-appdef-change', {bubbles: true}));
	}

	protected onSupRefChanged() {
		const supSrc = this._supRef.srcRef;
		if (supSrc && this._supRef.shortDesc.srcSt < 0) {
			//détection de la suppr de l'item.
			this.setSup(null);
			return;
		}
		if (supSrc && this._supEd && this._supEd.configs.srcRef === supSrc) return;
		this._clearSupEditor();
		if (!supSrc) return;
		this._buildSupEditor(this._supRef.shortDesc.itModel, this._docRef.shortDesc.itModel, supSrc);
		this.setSupPanelState(true);
		this.refresh();
		//Changement de doc => dispatch c-appdef-change
		this.appDef.sup = supSrc;
		this.dispatchEvent(new CustomEvent('c-appdef-change', {bubbles: true}));
	}

	protected _clearSupEditor() {
		if (this._supEd) {
			this._supEd.closeEditor();
			this._supEd.remove();
			this._supEd = null;
		}
	}

	protected _buildSupEditor(supModel: string, docModel: string, supSrc: srcRef, lastDatas?: JLastDatas) {
		const supType = this.wsp.wspMetaUi.getItemType(supModel);
		const docType = this.wsp.wspMetaUi.getItemType(docModel);
		const supEditor = docType.getSupportEditor(supType);
		if (supEditor) {
			this._supEd = this._supMain.appendChild(new ItemXmlEd().initialize({
				reg: this.reg,
				datasEditor: supEditor,
				srcRef: supSrc,
				initItemReg: (reg: IReg<IItemUiEnv>, itemEd: ItemXmlEd) => {
					reg.addToList("actions:wed:commonbar:end", "itemHamburger", 1, new SupItemHamburger(itemEd), 101);
				},
				lastDatas
			}));
		} else {
			POPUP.showNotifError("Aucun éditeur disponible pour ce support", this);
		}
	}

	// getSupportCrit(docSd: JSrcFields): ISearchCrit[] {
	// 	// 	const docModel = this.wsp.wspMetaUi.getItemType(docSd.itModel);
	// 	// 	return [
	// 	// 		new SearchLinkParents(this._docRef.srcRef, 'support'),
	// 	// 		new SearchItemModel(docModel.getSupportModels())
	// 	// 	]
	// 	// }
	// 	//
	// 	// protected async fetchSupports(): Promise<JSrcFields[]> {
	// 	// 	const docSd = this._docRef.shortDesc;
	// 	// 	const search = new SearchRequest().addFieldColumns(...this.wsp.getShortDescFields()).setMax(2000).setWhere(new SearchAnd(...this.getSupportCrit(docSd)));
	// 	// 	return WSP.fetchSearchJson<JSrcFields>(this.wsp, this, search.toXml());
	// 	// }

	protected async selectSupport(this: HTMLButtonElement, ev: Event) {
		try {
			const me = DOMSH.findHost<WspDocApp>(this);
			const docSd = me._docRef.shortDesc;
			if (!docSd) return;
			const docModel = me.wsp.wspMetaUi.getItemType(docSd.itModel);
			const supportModels = docModel.getSupportModels();
			const supportModelsSet = new Set(supportModels);

			const fastFindItemMk = me.reg.getSvc("fastFindItemMk") as (init: OFastFindItemsInit) => FastFindItem | null;
			if (fastFindItemMk) {
				const ct = fastFindItemMk({
					reg: me.reg,
					restrictions: new SearchAnd(
						new SearchLinkParents(me._docRef.srcRef, 'support'),
						new SearchItemModel(supportModels)
					),
					emptyBody: () => <span>Aucun support disponible pour ce contenu</span>,
					// BUG close popups returnValue non retournée via await POPUP.showMenuFromEvent(ct, ev || me).onNextClose<JSrcFields>();
					itemCreator: ItemCreator.setDefaultConfigFromReg(me.reg, {
						itemTypesTree: {
							itemTypeReducer: (acc: ItemType[], cur: ItemType) => {
								if (supportModelsSet.has(cur.getModel())) acc.push(cur);
								return acc;
							}
						},
						newContentOptions: {docShortDesc: docSd}
					}),
				} as OFastFindItemsInit);
				if (!ct) return;

				const supSd = await POPUP.showMenu<JSrcFields>(ct, {initHeight: "15em", viewPortY: "middle"}, this, {
					titleBar: {barLabel: {label: "Sélection du support"}}
				}).onNextClose();

				if (supSd) me._supRef.setSrcRef(SRC.srcRef(supSd), supSd);
			}

		} catch (e) {
			POPUP.showNotifError("Échec à la recherche des supports disponibles", this);
			ERROR.log(e);
		}
	}

	protected async createSupport(this: HTMLButtonElement, ev: Event) {
		const me = DOMSH.findHost<WspDocApp>(this);
		const docSd = me._docRef.shortDesc;
		const docModel = me.wsp.wspMetaUi.getItemType(docSd.itModel);
		const supportModelsSet = new Set(docModel.getSupportModels());
		const supSd = await ItemCreator.openCreateItem({
			reg: me.reg,
			itemTypesTree: {
				itemTypeReducer: (acc: ItemType[], cur: ItemType) => {
					if (supportModelsSet.has(cur.getModel())) acc.push(cur);
					return acc;
				}
			},
			newContentOptions: {
				docShortDesc: docSd
			}
		}, "Créer un support...", this, ev).onNextClose();
		if (supSd) me._supRef.setSrcRef(SRC.srcRef(supSd), supSd);
	}

	/** Init des extPoints du registre de l'app. */
	protected async initRegExtPoints() {
		const reg = this.reg;

		//#### Actions transversales sur les IShortDescCtx.
		function addShortDescAction(action: IAction<IShortDescCtx>, accelKey?: string, sortKey?: number) {
			reg.addToList("actions:wsp:shortDesc", action.getId(), 1, action, sortKey);
			if (accelKey) reg.addToList("accelkeys:wsp:shortDesc", accelKey, 1, action);
		}

		addShortDescAction(new FocusItemOrSpace(), "F3");
		addShortDescAction(ShortDescCopy.SINGLETON, "c-accel", -1);

		this.addWspActionsMain("actions:wspDocApp:main", "actions:wspDocApp");
		this.addGlobalAction("actions:wspDocApp:main", new OpenWspApp(), 70, "F8");

		//Config
		this.addWspActionsConfig("actions:wspDocApp:config");

		//Content
		this.addWspActionsContent("actions:wspDocApp:content");

		//Layers
		this.addWspActionsLayers("actions:wspDocApp:layers");

		function addBurgerItemAction(action: Action<IWedEditor | IShortDescCtx> | string) {
			if (typeof action === 'string')
				reg.addSvcToList("actions:wspDocAppp:support:burger", action, 1, action);
			else
				reg.addToList("actions:wspDocAppp:support:burger", action.getId(), 1, action);
		}

		addBurgerItemAction('src/delete');
		addBurgerItemAction(new SupItemDelete(this));

		registerWspProtocolsActions(reg);
	}
}

REG.reg.registerSkin('wsp-doc-app', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	[hidden] {
		display: none !important;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	#wspHeader {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		justify-content: center;
		user-select: none;
	}

	#wspIcon {
		height: 1.5rem;
	}

	#wspTi {
		margin: 0 .5rem;
		letter-spacing: .1rem;
		text-align: center;
		font-size: 1.2rem;
	}

	hr {
		border-top: 1px solid var(--border-color);
		border-inline-start: 1px solid var(--border-color);
		border-inline-end: none;
		border-bottom: none;
		margin: 0;
	}

	#docRoot {
		flex: 3;
		display: flex;
		min-height: 0;
		min-width: 0;
		overflow: hidden;
	}

	#docMain,
	#supMain {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#docMain {
		flex: 4;
	}

	#supMain {
		flex: 3;
		overflow: hidden;
	}

	wsp-finddoc {
		flex: 1;
	}

	.headPanel {
		display: flex;
		background-color: var(--tabf-bgcolor);
		color: var(--tabf-color);
		--pressed-bgcolor: var(--inv-pressed-bgcolor);
		padding: .3em;
		font-weight: bold;
		border-bottom: 2px solid var(--bgcolor);
	}

	.countPanel {
		display: flex;
		align-items: center;
		user-select: none;
	}

	.toggle {
		background: none;
		border: none;
		color: var(--tabf-color);
		font-weight: bold;
		user-select: none;
	}

	.toggle::before {
		content: "▼ ";
	}

	.toggle.closed::before {
		content: "▶ ";
	}

	.headTi {
		margin-inline-end: 2rem;
	}

	c-button:not([hidden]), c-action:not([hidden]) {
		display: inline-flex;
	}

	c-button, c-action {
		font-weight: normal;
		margin: 0 .5em;
	}

	c-resizer[c-orient="row"] {
		width: 1px;
	}

	c-resizer[c-orient="column"] {
		height: 1px;
	}

	wsp-generators {
		min-height: 3em;
	}

	wsp-src-drawer-inline {
		font-size: 1.2rem;
	}
`);


REG.reg.registerSkin('wsp-doc-app/srcptrInHead', 1, /* language=CSS */ `
	:host {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		user-select: none;
	}

	:host(.empty) {
		display: none;
	}

	.itemUri {
		color: var(--tabf-color);
		font-weight: bold;
	}

	.icon {
		filter: var(--filter);
		vertical-align: middle;
	}
`);

customElements.define('wsp-doc-app', WspDocApp);


class FindDocItem extends FastFindItem implements IView, IInfoConsumer {

	selectBtn: Button;

	protected _initialize(init: OFastFindItemsInit) {
		this.reg = this.findReg(init);
		this.reg.env.infoBroker?.addConsumer(this);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);


		//
		const btnCreate = ActionBtn.buildButton(new Action<IRegPointer<IWspUiEnv>>("createItem")
			.setLabel("Créer un contenu...")
			.requireEnabledPerm("create.item")
			.requireVisiblePerm("ui.app.wspDoc.create.doc")
			.setExecute((ctx) => {
				this.createItem.call(btnCreate);
			}), this, 'dialog');
		btnCreate.appendChild(JSX.asSvg(() => <svg viewBox="0 0 16 16" style="height: var(--icon-size); width: var(--icon-size); fill:var(--tabf-color)">
			<rect x="6" y="2" width="4" height="12"/>
			<rect x="2" y="6" width="12" height="4"/>
		</svg>));

		sr.appendChild(
			<div id="title">
				<span>Sélection du contenu</span>
				{btnCreate}
			</div>
		);

		this.search = <input type="search" placeholder="Filtrer..." aria-label="Filtrer" spellcheck="false" onchange={this.onSearchChange} oninput={this.onSearchChange} onkeydown={this.onKeydown}/> as HTMLInputElement;
		this.datasSearch = new SpaceTreeDataSearch(this.reg, '', false, init.srcFilter, [ITEM.AIR_PREFIX]);
		this.datasSearch.callbackSearching = this;
		this.request = new ReqFastFind(this.wsp).initReq(this.wsp.getShortDescDef(), '', init.restrictions);
		this.datasSearch.initSpTrDataSearch(this.request, 20, 30);

		const colDefs = [new GridColTreeDef('srcTree')
			.setDefaultSort(1, 'ascendant')
			.setFlex('1rem', 1, 1).setSortable(true)
			.setCellBuilder(new CellBuilderSrcIconCodeTitle(this.reg, this.wsp.wspMetaUi, false, this.wsp.srcUriItemsSortFn))];

		this.grid = new Grid().initialize({
			selType: "mono",
			columnDefs: colDefs,
			dataHolder: this.datasSearch,
			hideHeaders: true,
			emptyBody: () => {
				if (this.wsp.isInError) {
					return EMPTY_INERROR.cloneNode(true);
				} else if (this.searching || !this.wsp.isAvailable) {
					return EMPTY_LOADING.cloneNode(true);
				}
				return EMPTY_NORESULT.cloneNode(true);
			}
		});

		this.selectBtn = <Button ui-context="dialog" label="Sélectionner" disabled="" onclick={() => {
			const i = this.grid.getSelectedRow();
			if (i >= 0) this.onSelect(this.grid.dataHolder.getRow(i).rowKey);
		}}/> as Button;

		const refreshList = <Button icon="/@skin@/commons/icons/refresh.svg" title="Rafraîchir" onclick={() => {
			this.doSearch();
		}}/>

		const infoBuilder = this.reg.getSvc<(findDoc: FindDocItem) => Element | Promise<Element>>("FindDocItem.infoPanel");
		const infoPanel = infoBuilder ? infoBuilder(this) : null;
		if (infoPanel) {
			sr.appendChild(<div id="infoCtn">
				<div id="finderCtn" c-resizable="">
					<div id="search">
						{this.search}
						{refreshList}
					</div>
					{this.grid}
					<div id="footer">
						{this.selectBtn}
					</div>
				</div>
			</div>);

			function insertInfoPanel(elt: Element) {
				if (elt) {
					elt.setAttribute("c-resizable", "");
					sr.getElementById("infoCtn").append(
						<Resizer c-orient="row"/>,
						elt
					);
				}
			}

			if (infoPanel instanceof Element) {
				insertInfoPanel(infoPanel);
			} else {
				infoPanel.then(insertInfoPanel);
			}
		} else {
			sr.append(
				<div id="search">
					{this.search}
					{refreshList}
				</div>,
				this.grid,
				<div id="footer">
					{this.selectBtn}
				</div>
			);
		}
		this.grid.addEventListener("grid-select", (ev: CustomEvent) => {
			const row = this.grid.dataHolder.getRow(this.grid.getSelectedRow()) as GridDataRowJson<JSrcFieldsTree>;
			this.selectBtn.disabled = !row || row.rowDatas.ch != null;
		});
		this.grid.uiEvents.on("rowDblclick", (row: GridDataRowJson<JSrcFieldsTree> | null, ev: MouseEvent) => {
			if (row) this.onSelect(row.rowDatas);
		});

		this.grid.addEventListener('keypress', function (this: Grid, ev: KeyboardEvent) {
			if (ev.key === ' ' || ev.key === 'Enter') {
				const row = this.dataHolder.getRow(this.getSelectedRow()) as GridDataRowJson<JSrcFieldsTree>;
				if (row) DOMSH.findHost<FastFindItem>(this).onSelect(row.rowDatas);
			}
		});

	}

	protected viewShown: boolean;

	onViewShown() {
		if (this.viewShown) return;
		this.viewShown = true;
		this.doSearch();
	}

	onViewHidden(closed?: boolean): void {
		this.viewShown = false;
		if (!closed) this.datasSearch.setDatas([]); //clear
	}

	doSearch(): Promise<void> {
		this.searching = true;
		this.request.updateParams(this);
		return this.datasSearch.fetchSearchStart();
	}

	protected async createItem(this: Button) {
		const self = DOMSH.findHost(this) as FastFindItem;
		const itTypes = new Set(self.wsp.wspMetaUi.getDocModels());
		const shortDesc = await ItemCreator.openCreateItem({
			reg: self.reg,
			itemTypesTree: {
				itemTypeReducer: (acc: ItemType[], cur: ItemType) => {
					if (itTypes.has(cur.getModel())) acc.push(cur);
					return acc;
				}
			}
		}, "Créer un contenu...", this).onNextClose();
		if (shortDesc) self.onSelect(shortDesc);
	}

	onSelect(src: JSrcFieldsTree) {
		if (!src || ITEM.getSrcUriType(src.srcUri) === "space") return;
		DOMSH.findHost<WspDocApp>(this).setDoc(SRC.srcRef(src), src);
	}

	onInfo(info: IInfo) {
		if (info instanceof InfoCurrentItem && info.srcUri && !info.shortDesc) {
			//Apellé par un acte externe (pas de shortDesc) : un import de scar exemple.
			if (this.viewShown) {
				//console.log("FindDocItem:onInfo=>doSearch:::", info);
				this.doSearch().then(() => {
					this.datasSearch.visitRows((offset, src) => {
						if (src.srcUri === info.srcUri) this.grid.setSelectedRows(offset);
					})
				});
			}
		}
	}
}

REG.reg.registerSkin('wsp-finddoc', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		--row-inSel-unfocus-bgcolor: var(--row-inSel-bgcolor);
	}


	#title {
		background-color: var(--tabf-bgcolor);
		color: var(--tabf-color);
		--pressed-bgcolor: var(--inv-pressed-bgcolor);
		padding: .3em;
		font-weight: bold;
		user-select: none;
	}

	#createItem:not([hidden]) {
		display: inline-flex;
	}

	#createItem {
		font-weight: normal;
		margin: 0 1em;
	}

	#search {
		display: flex;
		min-height: min-content;
		min-width: min-content;
		background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg) var(--form-search-bgcolor);
		padding-inline-start: 1.2em;
		border-bottom: 1px solid var(--border-color);
	}

	#infoCtn {
		flex: 1;
		display: flex;
		min-height: min-content;
		min-width: min-content;
		background-color: var(--row-bgcolor);
	}

	#finderCtn {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	c-resizer {
		min-width: 1px;
	}

	input {
		flex: 1;
		background-color: var(--form-search-bgcolor);
		color: var(--form-color);
		border: none;
		padding: 2px;
		font-size: inherit;
	}

	input::placeholder {
		color: var(--fade-color);
		letter-spacing: 2px;
		font-size: .8em;
		font-style: italic;
	}

	input:focus::placeholder {
		color: transparent;
	}

	c-grid {
		flex: 1 1 16em;
		border: none;
		border-bottom: 1px solid var(--border-color);
	}

	#footer {
		display: flex;
		min-height: 0;
		min-width: 0;
		justify-content: flex-end;
	}
`);


customElements.define('wsp-finddoc', FindDocItem);

//
const EMPTY_LOADING = <section style="font-style:italic; text-align:center;">Chargement en cours...</section>;
const EMPTY_NORESULT = <section>
	<div style="font-style:italic; text-align:center;">Aucun contenu trouvé</div>
</section>;
const EMPTY_INERROR = <section>
	<div style="font-style:italic; text-align:center;">Configuration de l&apos;atelier en erreur</div>
</section>;

//


interface JLDWspDoc extends JLastDatas {
	docRef?: srcRef
	srcRef?: srcRef
	docView?: JLastDatas
	supRef?: srcRef
	supEd?: JLastDatas
	genCd?: string
	closed?: string /** "dsg" initiales des panels qui sont clos. */
}


class OpenWspApp extends Action<WspDocApp> {
	constructor() {
		super('openWsp');
		this._label = "Mode explorateur...";
		this._icon = "/@skin@/wsp/views/spaceTree/tree.svg";
		this.requireVisiblePerm("ui.wspApp");
	}

	execute(ctx: WspDocApp, ev?: Event): any | "noPreventDefault" {
		(desk as AppFrameDeskFeat).findAndOpenApp({u: ctx.universe.getId(), wsp: ctx.wsp.code, srcRef: ctx.appDef.doc} as JWspAppDef, ev);
	}
}

/**
 * Menu hamburger pour l'item support.
 * Les actions dans ce menu disposent d'un contexte qui implémente : IWedEditor & IShortDescCtx
 */
class SupItemHamburger extends ActionMenu<IWedEditor> {

	constructor(itemEd: ItemXmlEd) {
		super("itemHamb");
		this._icon = "/@skin@/commons/icons/hamburger.svg";
		this._label = "Actions générales sur cet item support";
		// On enrichi ItemXmlEd de l'api IShortDescCtx pour les actions sur l'item.
		const c = itemEd.view as any as IWedEditor & IShortDescCtx;
		if (!c.shortDescs) c.shortDescs = [itemEd.reg.env.longDesc];
		if (!c.emitter) c.emitter = itemEd;
		this._groupOrder = itemEd.reg.getPref("groupOrder.wsp.shortDesc", "");
		this._actionLists = "actions:wspDocAppp:support:burger actions:wsp:shortDesc";
	}
}

class SupItemDelete extends DuplicateSrc<IShortDescCtx> {

	constructor(public wspDocApp: WspDocApp) {
		super();
	}

	async execute(ctx: IShortDescCtx, ev?: Event): Promise<srcUri[]> {
		const res = await super.execute(ctx, ev);
		if (res && res.length > 0) this.wspDocApp.setSup(res[0]);
		return res;
	}
}

class WspDocAppInfoBroker extends InfoBrokerBasic {

	constructor(public wspDocApp: WspDocApp) {
		super();
	}

	async dispatchInfo(info: IInfo, from: IInfoProducer) {
		//console.log(info, from);
		if (info instanceof InfoFocusItem) {
			const isInSupport = this.isInSupportEd(from);
			const isInToc = this.wspDocApp._toc.isInToc(info.srcRef);
			if (isInSupport || !isInToc) {
				const itemViewer = new ItemViewerSingle({mainViewCode: ":hideWspStruct:"});
				const root = <ViewsContainer î={{views: [itemViewer]}} style="flex:1;display:flex;min-height:0;min-width:0;flex-direction:column;"/> as ViewsContainer;
				const subReg = REG.createSubReg<IWspUiEnv>(this.wspDocApp.reg);

				// Fermeture de la popup en cas de remove de l'uri affichée
				subReg.env.place.eventsMgr.on("wspUriChange", (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => {
					if (msg.wspCd !== subReg.env.wsp.code) return;
					if (msg.type === EWspChangesEvts.r && subReg.env.uiRoot && itemViewer.longDesc?.srcUri && SRC.isSubUriOrEqual(msg.srcUri, itemViewer.longDesc.srcUri)) {
						const leafName = SRC.extractLeafFromUri(itemViewer.longDesc.srcUri);
						POPUP.showNotifInfo(`L'item "${leafName}" a été supprimé ou renommé.`, document.body, {autoHide: 2000});
						(subReg.env.uiRoot as IPopupable<any>).close();
					}
				})

				subReg.env.infoBroker = new ItemEditInDialogInfoBroker(this.wspDocApp.reg);
				await itemViewer.initViewer(subReg, root, null, info.srcRef);
				if (info instanceof InfoFocusNodeInItem)
					subReg.env.infoBroker.dispatchInfo(new InfoFocusNodeInItem(info.xpath, info.srcRef), this);
				subReg.env.uiRoot = POPUP.showDialog(root, this.wspDocApp, {
					titleBar: {barLabel: {label: isInSupport ? "Paramétrage du support" : "Fragment du contenu"}, closeButton: {uiContext: 'dialog', label: "Fermer"}},
					initWidth: '90%',
					initHeight: '90%',
					resizer: {}
				});
				info.handled = true;
				return;
			}
		}
		super.dispatchInfo(info, from);
	}

	isInSupportEd(from: IInfoProducer): boolean {
		if (from instanceof HTMLElement) {
			const ed = DOMSH.findFlatParentEltOrSelf(from, this.wspDocApp, (n: Node): n is ItemXmlEd => n instanceof ItemXmlEd);
			if (ed && ed.parentNode === this.wspDocApp._supMain) return true;
		}
		return false;
	}
}

function hideView(view: IView, hide: boolean) {
	if (DOM.setHidden(view, hide)) {
		if (hide) VIEWS.onViewHidden(view);
		else VIEWS.onViewShown(view);
	}
}